import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'my-utils';

import { updateOnlineCourseUserProgress } from 'my-actions/OnlineCourseUserActions';

export function useOnlineCourseProgressManager(onlineCourseUser) {
  const dispatch = useDispatch();
  const updateProgressStatus = useSelector(
    state => state.onlineCourseUsers.status[`update_progress_${onlineCourseUser?.id}`],
  );

  const canSave = onlineCourseUser?.id && !updateProgressStatus?.error;
  const saveProgress = useCallback(
    updates => {
      // console.log('PG SAVE:', ...updates);
      dispatch(updateOnlineCourseUserProgress(onlineCourseUser.id, updates));
    },
    [dispatch, onlineCourseUser?.id],
  );

  const [, forceUpdate] = useState();
  const progressTracker = useRef();
  if (!progressTracker.current) {
    progressTracker.current = new ProgressTracker({
      onlineCourseUser,
      updater: canSave && saveProgress,
      forceRerender: () => forceUpdate({}),
    });
  }
  useEffect(() => {
    progressTracker.current.setUpdater(canSave && saveProgress);
    progressTracker.current.setOCU(onlineCourseUser);
  }, [canSave, saveProgress, onlineCourseUser]);

  return {
    enqueueProgressSave: progressTracker.current.enqueueProgressSave,
    getProgress: progressTracker.current.getProgress,
  };
}

class ProgressTracker {
  pendingUpdates = [];

  constructor({ forceRerender, onlineCourseUser, updater }) {
    this.ocu = onlineCourseUser;
    this.updater = updater;
    this.forceRerender = forceRerender;
    this.updateProgressMap();
  }

  setOCU(ocu) {
    if (ocu === this.ocu) return;
    if (ocu && this.ocu && ocu.progress !== this.ocu.progress) this.pendingUpdates = [];
    this.ocu = ocu;
    this.updateProgressMap();
    this.forceRerender();
  }
  updateProgressMap() {
    this.progressMap = Object.fromEntries(
      [...(this.ocu?.progress?.completed || []), ...this.pendingUpdates].map(a => [a.activity_id, a]),
    );
    // console.log('PG MAP', this.progressMap);
  }

  getProgress = activityId => this.progressMap[activityId];

  setUpdater = updater => {
    const prevUpdater = this.updater;
    if (prevUpdater === updater) return;
    if (prevUpdater && this.pendingUpdates.length) this.savePendingUpdates();
    this.updater = updater;
    if (!prevUpdater && this.pendingUpdates.length) this.savePendingUpdates();
  };

  enqueueProgressSave = (activityId, params, { immediate } = {}) => {
    const progress = this.getProgress(activityId);

    let newPendingUpdates = this.pendingUpdates;
    if (params?.is_current) {
      newPendingUpdates = newPendingUpdates.map(pu => ({ ...pu, is_current: undefined }));
    }

    const progressUpdate = {
      ...progress,
      activity_id: activityId,
      ...params,
    };

    if (progressUpdate.update_duration) {
      progressUpdate.duration = (progressUpdate.duration || 0) + progressUpdate.update_duration;
      delete progressUpdate.update_duration;
    }

    newPendingUpdates = [
      progressUpdate,
      ...newPendingUpdates.filter(u => u.activity_id !== progressUpdate.activity_id),
    ];

    // console.log('PG UPDATE', progress, params, progressUpdate);
    this.pendingUpdates = newPendingUpdates;
    this.progressMap[progressUpdate.activity_id] = progressUpdate;
    this.forceRerender();

    if (immediate || !progress) {
      this.savePendingUpdates();
    } else {
      this.debouncedSavePendingUpdates();
    }
  };

  savePendingUpdates = () => {
    if (!this.updater || !this.pendingUpdates.length) return;
    this.debouncedSavePendingUpdates.cancel();
    this.updater(this.pendingUpdates);
    this.pendingUpdates = [];
  };

  debouncedSavePendingUpdates = debounce(this.savePendingUpdates, 1000);
}
